import { useEffect, useState } from "react";

interface KbEvent { t: number; v: boolean; h: number; s?: number; }
/** Registration/delivery forensics — see __kbDiag.src in useKeyboardOffset.ts.
 *  Added for issue #329: without this, `med.vis=—` cannot distinguish "Median
 *  absent" from "Median present but never delivering an event". */
interface KbSrc {
  medianPresent: boolean;
  keyboardKeys: string[] | null;
  payloadKeys: string[] | null;
  listenCalls: number;
  fires: number;
  firstFireMs: number | null;
  dupBursts: number;
  persistent: boolean;
  stringRetry: string;
  reArmError: string | null;
  fallbackArmed: boolean;
  path: string;
  info: unknown;
}
interface KbDiag {
  median: { visible: boolean | null; keyboardHeight: number | null; visibleHeight: number | null };
  vv: { innerHeight: number | null; height: number | null; offsetTop: number | null };
  stable: { value: number; phase: string; raw: number } | null;
  chatinput: { propOpen: boolean; rectTop: number; rectBottom: number } | null;
  events: KbEvent[];
  src?: KbSrc;
  animState?: string;
  fullHeight?: number;
  /** THIS sheet's own geometry inputs — see writeSheetGeomDiag in
   *  BottomSheet.tsx. Distinct from `stable`/`fullHeight`, which belong to
   *  whichever hook instance wrote last (usually Index's shell). */
  sheetGeom?: {
    offset: number;
    fullHeight: number;
    overlayHeight: string;
    top: string;
    keyboardOpen: boolean;
  };
  /** iOS form accessory bar state — see useHideKeyboardAccessory.ts (issue #331).
   *  "hidden"/"url-fallback" = the native call went through; "pending" = bridge
   *  not injected yet; "no-bridge" = not inside Median. */
  accessory?: string;
}

interface LiveRow {
  vvHeight: number | null;
  vvHeightVar: string | null;
  innerHeight: number | null;
  docHeight: number | null;
  ciTopRect: number | null;
  ciBotRect: number | null;
  fullHeight: number | null;
  scrollY: number | null;
}

const isDebugEnabled = () =>
  typeof window !== "undefined" &&
  (localStorage.getItem("kbDebug") === "1" ||
    new URLSearchParams(window.location.search).get("debug") === "1");

export default function KBDebugStrip() {
  const [diag, setDiag] = useState<KbDiag | null>(null);
  // Flipped by the ✕ button. isDebugEnabled() is re-read every render, so
  // clearing the flag plus this state change hides the strip immediately —
  // no reload, which matters because on the Median shell a reload is exactly
  // the expensive step (fresh context) this strip exists to avoid.
  const [, forceRender] = useState(0);
  // Mirrors the flag so the strip can arm WITHOUT a reload. The ✕ already hid
  // it in place (kbDebugStripOff.test.tsx) but arming was reload-only, because
  // the polling effects below early-return and had no dependency that could
  // change. On the Median shell a reload is a fresh browsing context — the
  // expensive step this strip exists to avoid — and it also destroys the
  // keyboard state you were trying to catch.
  const [armed, setArmed] = useState(isDebugEnabled);
  useEffect(() => {
    const sync = () => setArmed(isDebugEnabled());
    window.addEventListener("kin:kb-debug", sync);
    return () => window.removeEventListener("kin:kb-debug", sync);
  }, []);
  const [sheet, setSheet] = useState<{
    overlayBottom: number;
    scrollTop: number;
    scrollMax: number;
    scrollportBottom: number;
    focusedBottom: number | null;
    vvH: number | null;
    vvTop: number | null;
  } | null>(null);
  const [live, setLive] = useState<LiveRow>({ vvHeight: null, vvHeightVar: null, innerHeight: null, docHeight: null, ciTopRect: null, ciBotRect: null, fullHeight: null, scrollY: null });

  useEffect(() => {
    if (!armed || typeof window === "undefined") return;
    const id = setInterval(() => {
      const src = (window as any).__kbDiag;
      if (src) setDiag({ median: { ...src.median }, vv: { ...src.vv }, stable: src.stable ? { ...src.stable } : null, chatinput: src.chatinput ? { ...src.chatinput } : null, events: Array.isArray(src.events) ? [...src.events] : [], src: src.src ? { ...src.src } : undefined, animState: src.animState, fullHeight: src.fullHeight, accessory: src.accessory, sheetGeom: src.sheetGeom ? { ...src.sheetGeom } : undefined });
    }, 100);
    return () => clearInterval(id);
  }, [armed]);

  useEffect(() => {
    if (!armed || typeof window === "undefined") return;
    const id = setInterval(() => {
      const ciEl = document.querySelector("[data-chat-input]");
      const r = ciEl?.getBoundingClientRect();
      setLive({
        vvHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
        vvHeightVar: document.documentElement.style.getPropertyValue("--vv-height") || null,
        innerHeight: window.innerHeight,
        docHeight: Math.round(document.documentElement.clientHeight),
        ciTopRect: r ? Math.round(r.top) : null,
        ciBotRect: r ? Math.round(r.bottom) : null,
        fullHeight: (window as any).__kbDiag?.fullHeight ?? null,
        scrollY: Math.round(window.scrollY),
      });
      // Sheet forensics (Aug 21, "can't even scroll to the last field").
      // Every number here is MEASURED from the live DOM, none derived from
      // the keyboard hooks — so a disagreement between this line and the
      // stbl/--vv-h line IS the diagnosis.
      const overlay = document.querySelector<HTMLElement>("[data-kin-sheet-overlay]");
      const sc = document.querySelector<HTMLElement>("[data-kin-sheet-scroll]");
      const ae = document.activeElement as HTMLElement | null;
      const focusable = ae && (ae.tagName === "TEXTAREA" || ae.tagName === "INPUT") ? ae : null;
      setSheet(
        overlay && sc
          ? {
              overlayBottom: Math.round(overlay.getBoundingClientRect().bottom),
              scrollTop: Math.round(sc.scrollTop),
              scrollMax: Math.round(sc.scrollHeight - sc.clientHeight),
              scrollportBottom: Math.round(sc.getBoundingClientRect().bottom),
              focusedBottom: focusable ? Math.round(focusable.getBoundingClientRect().bottom) : null,
              vvH: window.visualViewport ? Math.round(window.visualViewport.height) : null,
              vvTop: window.visualViewport ? Math.round(window.visualViewport.offsetTop) : null,
            }
          : null,
      );
    }, 200);
    return () => clearInterval(id);
  }, [armed]);

  if (!isDebugEnabled() || !diag) return null;

  const m = diag.median;
  const v = diag.vv;
  const s = diag.stable;
  const ci = diag.chatinput;
  const evs = diag.events;
  const src = diag.src;
  const t0 = evs[0]?.t ?? 0;
  const evStr = evs.length === 0
    ? "—"
    : evs.map(e => `+${e.t - t0}:${e.v ? "T" : "F"}:${e.h}${e.s !== undefined ? `#${e.s}` : ""}`).join(" ");
  // The line that answers issue #329's question directly: did listen() ever
  // fire, and if not, is the bridge even there?
  const srcStr = !src
    ? "—"
    : `med?=${src.medianPresent ? "Y" : "N"} listen=${src.listenCalls} fires=${src.fires}` +
      ` t1=${src.firstFireMs ?? "—"} dup=${src.dupBursts}${src.persistent ? "(persist)" : ""}` +
      ` str=${src.stringRetry} armed=${src.fallbackArmed ? "Y" : "N"} path=${src.path}` +
      ` acc=${diag.accessory ?? "—"}`;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      minHeight: 55,
      overflow: "visible",
      background: "rgba(0,0,0,0.95)",
      color: "#fff",
      fontFamily: "monospace",
      fontSize: 10,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      // 46 on the right keeps the readings clear of the ✕ button.
      padding: "4px 46px 4px 8px",
      gap: 3,
      // Display-only: the strip sits at z-9999 over the ChatHeader and would
      // otherwise cover the Kin logo that is its own 5-tap off switch.
      // pointer-events is inherited, so every row below is transparent to hit
      // testing too. If any row is ever made interactive, give THAT element
      // pointerEvents: "auto" individually — never lift this from the root.
      pointerEvents: "none",
    }}>
      {/* The one interactive element, per the note above. The 5-tap toggle
          still exists but the strip COVERS the logo it lives on — aiming a
          blind 5-tap through an opaque overlay is not an off switch. Tapping
          this clears the flag and hides the strip in place; 5-tap the logo
          to bring it back. */}
      <button
        aria-label="Hide debug strip"
        onClick={() => {
          localStorage.removeItem("kbDebug");
          forceRender((n) => n + 1);
        }}
        style={{
          pointerEvents: "auto",
          position: "absolute",
          top: 4,
          right: 6,
          width: 34,
          height: 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.14)",
          border: "1px solid rgba(255,255,255,0.35)",
          borderRadius: 8,
          color: "#fff",
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
      <div>med.vis={String(m.visible ?? "—")} | med.kbH={m.keyboardHeight ?? "—"} | med.visH={m.visibleHeight ?? "—"}</div>
      <div>vv.iH={v.innerHeight ?? "—"} | vv.h={v.height ?? "—"} | vv.oT={v.offsetTop ?? "—"} | stbl={s?.value ?? "—"} | ph={s?.phase ?? "—"} | {diag?.animState ?? "idle"}</div>
      <div>ci.open={String(ci?.propOpen ?? "—")} | ci.top={ci?.rectTop ?? "—"} | ci.bot={ci?.rectBottom ?? "—"}</div>
      <div style={{ wordBreak: "break-all", background: "rgba(255,80,80,0.18)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>src: {srcStr}</div>
      {src && (src.keyboardKeys || src.payloadKeys || src.reArmError) && (
        <div style={{ wordBreak: "break-all", background: "rgba(255,80,80,0.10)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>
          kb.keys=[{src.keyboardKeys?.join(",") ?? "—"}] | payload=[{src.payloadKeys?.join(",") ?? "—"}]{src.reArmError ? ` | rearmErr=${src.reArmError}` : ""}
        </div>
      )}
      <div style={{ wordBreak: "break-all", background: "rgba(255,200,0,0.15)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>ev({evs.length}): {evStr}</div>
      {sheet && (
        <div style={{ background: "rgba(120,255,120,0.18)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>
          sheet: ovB={sheet.overlayBottom} spB={sheet.scrollportBottom} sc={sheet.scrollTop}/{sheet.scrollMax} fB={sheet.focusedBottom ?? "—"} vv.h={sheet.vvH ?? "—"} vv.oT={sheet.vvTop ?? "—"}
        </div>
      )}
      {diag?.sheetGeom && (
        /* The sheet's OWN hook inputs, next to the measured row above. If
           sg.oh matches ovB but both are short, the keyboard hooks are wrong;
           if they disagree, the panel CSS is. sg.off is the offset the OPEN
           SHEET is subtracting — the one the stbl row cannot attribute. */
        <div style={{ background: "rgba(255,120,255,0.18)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>
          sg: off={diag.sheetGeom.offset} kbOpen={String(diag.sheetGeom.keyboardOpen)} full={diag.sheetGeom.fullHeight} oh={diag.sheetGeom.overlayHeight} top={diag.sheetGeom.top}
        </div>
      )}
      <div style={{ background: "rgba(0,200,255,0.15)", marginLeft: -8, marginRight: -8, paddingLeft: 8 }}>build: {String((window as any).__KIN_BUILD__ ?? "—")} | fullH: {live.fullHeight ?? "—"} | liveH: {live.docHeight ?? "—"} | stbl: {diag?.stable?.value ?? "—"} | --vv-h: {live.vvHeightVar ?? "—"} | scrollY: {live.scrollY ?? "—"} | ci.bot: {live.ciBotRect ?? "—"}</div>
    </div>
  );
}
